import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { MapPin, Pencil, X, Crosshair, MagnifyingGlass, CaretRight } from "@phosphor-icons/react";

const STORAGE_KEY = "np:user_location";

// Fallback location the user can override. Portland, OR is a warm default that
// resolves cleanly and matches the copy in the product brief.
export const DEFAULT_LOCATION = {
  lat: 45.5152, lon: -122.6784,
  city: "Portland", state: "Oregon", country: "United States",
  display_name: "Portland, Oregon, United States of America",
  source: "default",
};

export function readLocation() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return DEFAULT_LOCATION;
}

function writeLocation(loc) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(loc)); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent("np:location-changed", { detail: loc }));
}

function displayName(loc) {
  if (!loc) return "Set your location";
  return loc.display_name || [loc.city, loc.state, loc.country].filter(Boolean).join(", ") || "Set your location";
}

function LocationPickerModal({ current, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not available in this browser");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lon } = pos.coords;
      try {
        const { data } = await http.post("/nature/geo/lookup", { lat, lon });
        const loc = {
          lat, lon,
          city: data.city || data.village || data.locality,
          state: data.state, country: data.country,
          display_name: data.display_name || [data.city, data.state, data.country].filter(Boolean).join(", "),
          source: "gps",
        };
        writeLocation(loc);
        onSaved?.(loc);
        toast.success(`Location set to ${displayName(loc)}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Reverse geocode failed");
      } finally { setBusy(false); }
    }, (err) => {
      setBusy(false);
      toast.error(err.code === 1 ? "Permission denied — enable location in your browser" : "Could not read your location");
    }, { enableHighAccuracy: false, timeout: 12000, maximumAge: 3600000 });
  };

  const search = async () => {
    if (query.trim().length < 3) return;
    setBusy(true);
    try {
      const q = encodeURIComponent(query.trim());
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&addressdetails=1&q=${q}`);
      const arr = await r.json();
      setResults(arr || []);
    } catch (e) {
      toast.error("Search failed");
    } finally { setBusy(false); }
  };

  const pickResult = async (r) => {
    const lat = parseFloat(r.lat), lon = parseFloat(r.lon);
    setBusy(true);
    try {
      const { data } = await http.post("/nature/geo/lookup", { lat, lon });
      const loc = {
        lat, lon,
        city: data.city || data.village || data.locality || r.address?.city || r.address?.town,
        state: data.state || r.address?.state,
        country: data.country || r.address?.country,
        display_name: data.display_name || r.display_name,
        source: "search",
      };
      writeLocation(loc);
      onSaved?.(loc);
      toast.success(`Location set to ${displayName(loc)}`);
    } catch (e) {
      // Even if backend fails, we still have a workable location from the search hit.
      const loc = {
        lat, lon,
        city: r.address?.city || r.address?.town || r.address?.village,
        state: r.address?.state, country: r.address?.country,
        display_name: r.display_name, source: "search",
      };
      writeLocation(loc);
      onSaved?.(loc);
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-[#241209]/70 backdrop-blur-sm flex items-start justify-center px-4 pt-20"
      data-testid="location-picker-modal"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-[#F5EBDC] rounded-2xl w-full max-w-md overflow-hidden border border-black/15 shadow-2xl">
        <div className="bg-[#2A1F14] text-[#E4A15C] px-4 py-3 flex items-center gap-2">
          <MapPin size={16} weight="fill" />
          <div className="flex-1 font-mono-tech text-[10px] tracking-[0.32em] uppercase">Your location</div>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-[#E4A15C]/10" data-testid="location-picker-close">
            <X size={15} weight="bold" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-sm text-[#241209]/85">
            Currently: <b>{displayName(current)}</b>
          </div>

          <button
            onClick={useMyLocation}
            disabled={busy}
            data-testid="location-use-gps"
            className="w-full inline-flex items-center gap-2 rounded-full border border-[#B85F2A] bg-[#B85F2A] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90 disabled:opacity-40"
          >
            <Crosshair size={13} weight="fill" /> {busy ? "Locating…" : "Use my current location (GPS)"}
          </button>

          <div className="pt-1">
            <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80 mb-1">
              Or search for a place
            </div>
            <div className="flex gap-1">
              <input
                data-testid="location-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder="City, state, or country…"
                className="flex-1 rounded-full border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#B85F2A]"
              />
              <button
                onClick={search}
                disabled={busy || query.trim().length < 3}
                data-testid="location-search-btn"
                className="rounded-full border border-black/15 bg-white px-3 py-2 hover:bg-black/5 disabled:opacity-40"
              >
                <MagnifyingGlass size={13} weight="bold" />
              </button>
            </div>
            {results.length > 0 && (
              <div className="mt-2 space-y-1 max-h-60 overflow-y-auto">
                {results.map((r, i) => (
                  <button
                    key={r.place_id || i}
                    onClick={() => pickResult(r)}
                    data-testid={`location-result-${i}`}
                    className="w-full text-left rounded-lg border border-black/10 bg-white px-3 py-2 hover:bg-[#FFF6E5] text-xs"
                  >
                    {r.display_name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LocationPill({ compact = false }) {
  const [loc, setLoc] = useState(readLocation);
  const [open, setOpen] = useState(false);
  const bootstrappedRef = useRef(false);

  // Auto-bootstrap on first visit: if the user hasn't set a location AND allows
  // GPS silently (already granted), try to fill it once. Otherwise leave the
  // default in place — the user can click Change.
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    if (loc?.source && loc.source !== "default") return;
    if (!navigator.geolocation || !navigator.permissions) return;
    navigator.permissions.query({ name: "geolocation" }).then((p) => {
      if (p.state !== "granted") return; // don't prompt automatically
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const { data } = await http.post("/nature/geo/lookup", {
            lat: pos.coords.latitude, lon: pos.coords.longitude,
          });
          const next = {
            lat: pos.coords.latitude, lon: pos.coords.longitude,
            city: data.city || data.village || data.locality,
            state: data.state, country: data.country,
            display_name: data.display_name,
            source: "gps",
          };
          writeLocation(next);
          setLoc(next);
        } catch { /* ignore */ }
      }, () => { /* ignore */ }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 3600000 });
    }).catch(() => { /* ignore */ });
  }, [loc]);

  // Cross-tab sync via storage event + same-tab custom event
  useEffect(() => {
    const onChange = () => setLoc(readLocation());
    window.addEventListener("np:location-changed", onChange);
    window.addEventListener("storage", (e) => e.key === STORAGE_KEY && onChange());
    return () => {
      window.removeEventListener("np:location-changed", onChange);
    };
  }, []);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="location-pill"
        title="Change your location"
        className={`inline-flex items-center gap-1 rounded-full border border-[#E4A15C]/40 bg-[#241209]/70 hover:bg-[#241209] transition text-[#E4A15C] font-mono-tech tracking-[0.22em] uppercase ${
          compact ? "px-2 py-0.5 text-[9px]" : "px-3 py-1 text-[10px]"
        }`}
      >
        <MapPin size={compact ? 10 : 12} weight="fill" />
        <span className="max-w-[220px] truncate normal-case tracking-normal">
          {displayName(loc)}
        </span>
        <span className="inline-flex items-center gap-0.5 text-[9px] opacity-80">
          Change <CaretRight size={9} weight="bold" />
        </span>
      </button>

      {open && (
        <LocationPickerModal
          current={loc}
          onClose={() => setOpen(false)}
          onSaved={(next) => { setLoc(next); setOpen(false); }}
        />
      )}
    </>
  );
}
